import { Link } from "@tanstack/react-router";
import { Clock } from "lucide-react";

// Canonical lodge operating hours — keep in sync everywhere they render.
export const LODGE_HOURS = {
  inSeason: { label: "In season", value: "6:00a – 8:00p" },
  offSeason: { label: "Off season", value: "Closed" },
  seasonNote: "Fishing season runs May – September.",
} as const;

// Alaska Time zone abbreviation (AKDT during DST, AKST otherwise), resolved
// from the runtime's IANA data so it stays correct across the DST boundary.
export function getAlaskaTzAbbr(date = new Date()): "AKDT" | "AKST" {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Anchorage",
    timeZoneName: "short",
  }).formatToParts(date);
  const name = parts.find((p) => p.type === "timeZoneName")?.value ?? "AKST";
  return name === "AKDT" ? "AKDT" : "AKST";
}

export function TzBadge({ className = "" }: { className?: string }) {
  const abbr = getAlaskaTzAbbr();
  const offset = abbr === "AKDT" ? "−8" : "−9";
  return (
    <abbr
      title={`Alaska Time (${abbr} — America/Anchorage, UTC${offset})`}
      className={`rounded bg-[var(--electric)]/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--electric)] no-underline ${className}`}
    >
      {abbr}
    </abbr>
  );
}

/**
 * Full hours card — used in the footer and on the contact page.
 * `variant="light"` renders on light backgrounds; `dark` (default) is for the footer.
 */
export function LodgeHoursCard({
  variant = "dark",
  showContactLink = true,
}: {
  variant?: "dark" | "light";
  showContactLink?: boolean;
}) {
  const isDark = variant === "dark";
  const container = isDark
    ? "divide-y divide-[var(--electric)]/10 rounded-2xl border border-[var(--electric)]/15 bg-[var(--navy)]/40 text-sm"
    : "divide-y divide-border rounded-2xl border border-border bg-background text-sm";
  const term = isDark ? "text-[var(--offwhite)]" : "text-foreground";
  const val = isDark
    ? "font-medium tabular-nums text-[var(--offwhite)]"
    : "font-medium tabular-nums text-foreground";
  const note = isDark
    ? "mt-3 text-xs leading-relaxed text-[var(--offwhite-warm)]"
    : "mt-3 text-xs leading-relaxed text-muted-foreground";
  const linkCls = isDark
    ? "underline decoration-[var(--electric)]/60 underline-offset-4 hover:text-[var(--electric)]"
    : "underline decoration-[var(--color-ember)]/60 underline-offset-4 hover:text-[var(--color-ember)]";

  return (
    <div className="w-full min-w-0">
      <dl className={`${container} w-full min-w-0 overflow-hidden`}>
        <div className="flex min-h-12 flex-wrap items-center justify-between gap-x-3 gap-y-1 px-4 py-3">
          <dt className={`${term} min-w-0 break-words`}>{LODGE_HOURS.inSeason.label}</dt>
          <dd className={`flex min-w-0 flex-wrap items-center justify-end gap-x-1 gap-y-1 ${val}`}>
            <span className="tabular-nums">{LODGE_HOURS.inSeason.value}</span>
            <TzBadge className="shrink-0" />
          </dd>
        </div>
        <div className="flex min-w-0 flex-col items-start gap-2 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-baseline sm:justify-between sm:gap-x-3">
          <dt className={`${term} min-w-0 break-words`}>{LODGE_HOURS.offSeason.label}</dt>
          <dd className={`flex min-w-0 max-w-full flex-wrap items-baseline gap-2 sm:justify-end ${val}`}>
            <span className="inline-flex max-w-full shrink-0 items-center whitespace-nowrap rounded-full border border-current/30 px-2.5 py-1 text-xs font-semibold uppercase leading-none tracking-[0.12em] opacity-80">
              {LODGE_HOURS.offSeason.value}
            </span>
            {showContactLink ? (
              <Link
                to="/contact"
                className={
                  isDark
                    ? "inline-flex max-w-full shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-[var(--electric)] px-2.5 py-1 text-xs font-semibold uppercase leading-none tracking-[0.12em] text-[var(--electric-foreground)] shadow-[var(--shadow-electric)] transition-transform hover:-translate-y-0.5"
                    : "inline-flex max-w-full shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-[var(--color-ember-deep)] px-2.5 py-1 text-xs font-semibold uppercase leading-none tracking-[0.12em] text-white shadow-sm transition-colors hover:bg-[var(--color-ember-deep)]/90"
                }
              >
                <span className="truncate">Message us</span> <span aria-hidden>→</span>
              </Link>
            ) : null}
          </dd>
        </div>



      </dl>
      {showContactLink ? (
        <p className={note}>
          Typical reply within <strong className={isDark ? "text-[var(--offwhite)]" : "text-foreground"}>24 hours</strong>{" "}
          (often the same business day).
        </p>
      ) : null}
      <p className={note}>
        {LODGE_HOURS.seasonNote}
        <br />
        All times Alaska Time · America/Anchorage (AKDT UTC−8 in summer, AKST UTC−9 in winter).
      </p>
    </div>
  );
}

/**
 * Compact inline hours strip — used in the site header.
 * Renders in-season hours + TZ badge on one line, with an off-season note.
 */
export function LodgeHoursInline({ className = "" }: { className?: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs text-muted-foreground ${className}`}
      aria-label="Lodge hours"
    >
      <Clock aria-hidden className="h-3.5 w-3.5" />
      <span className="tabular-nums">{LODGE_HOURS.inSeason.value}</span>
      <TzBadge />
      <span className="hidden xl:inline text-muted-foreground/70">
        · Off season closed
      </span>
    </span>
  );
}
